import { Box, useTheme } from "@mui/material";
import { forwardRef, useState, useEffect } from "react";
import { Control, Controller, FieldPath, FieldValues, useWatch } from "react-hook-form";

import { AgencyPickerLayout } from "./AgencyPickerLayout";

interface AgencyFragment {
  id: string;
  name?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  city?: string | null;
  state?: string | null;
  county?: string | null;
  mailingAddress?: string | null;
}

interface PrimaryAgencyPickerProps {
  name: FieldPath<any>;
  control: Control<any>;
  facilityState?: string;
  facilityCounty?: string;
  facilityLatitude?: number | null;
  facilityLongitude?: number | null;
  disabled?: boolean;
  helperText: string;
  PickerComponent: React.ComponentType<any>;
  existingAgency?: AgencyFragment | null;
  pickerDescription?: string;
  /** Override the picker's default label (e.g. "CUPA" instead of "LEPC") */
  label?: string;
}

export const PrimaryAgencyPicker = forwardRef<HTMLDivElement, PrimaryAgencyPickerProps>(
  function PrimaryAgencyPicker(
    {
      name,
      control,
      facilityState,
      facilityCounty,
      facilityLatitude,
      facilityLongitude,
      disabled = false,
      PickerComponent,
      existingAgency,
      helperText,
      pickerDescription,
      label,
    },
    ref
  ) {
    const theme = useTheme();

    // Local state for the selected agency object (for display purposes)
    const [selectedAgency, setSelectedAgency] = useState<any | null>(null);

    // Watch the form field value to sync with local state
    const fieldValue = useWatch({ control, name });

    // Initialize selected agency from GraphQL data when it becomes available
    useEffect(() => {
      if (existingAgency && !selectedAgency && fieldValue === existingAgency.id) {
        setSelectedAgency(existingAgency);
      }
    }, [existingAgency, selectedAgency, fieldValue]);

    // Clear local state when form field is cleared
    useEffect(() => {
      if (fieldValue === null && selectedAgency) {
        setSelectedAgency(null);
      }
    }, [fieldValue, selectedAgency]);

    // Update selected object when picker changes
    const handlePickerChange = (agency: any) => {
      setSelectedAgency(agency);
      return agency;
    };

    return (
      <Box ref={ref}>
        <AgencyPickerLayout
          selectedAgency={selectedAgency}
          facilityLatitude={facilityLatitude}
          facilityLongitude={facilityLongitude}
          pickerDescription={pickerDescription}
        >
          <Controller
            name={name}
            control={control}
            render={({ field, fieldState }) => (
              <PickerComponent
                {...field}
                {...fieldState}
                facilityState={facilityState}
                facilityCounty={facilityCounty}
                facilityLatitude={facilityLatitude}
                facilityLongitude={facilityLongitude}
                disabled={disabled}
                helperText={helperText}
                label={label}
                onChange={(agency: any) => {
                  field.onChange(agency?.id ?? null);
                  handlePickerChange(agency);
                }}
                value={selectedAgency}
              />
            )}
          />
        </AgencyPickerLayout>
      </Box>
    );
  }
);
